import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import {
  getCinematicSettingsForModel,
  DRONE_MOVES_GUIDE,
  type DroneCinematicSettings,
} from "@client/lib/droneCinematicSettings";

type Drone = {
  id: string;
  name: string;
  model: string | null;
};

type ViewMode = "settings" | "moves";

export default function DroneCinematic() {
  const { droneId } = useParams<{ droneId: string }>();
  const { data: drones = [], isLoading } = useQuery<Drone[]>({ queryKey: ["/api/drones"] });

  const drone = drones.find((d) => d.id === droneId);

  const settings: DroneCinematicSettings | null = useMemo(() => {
    const model = drone?.model ?? null;
    const name = drone?.name ?? null;
    return getCinematicSettingsForModel(model, name);
  }, [drone?.model, drone?.name]);

  const [mode, setMode] = useState<ViewMode>(settings ? "settings" : "moves");

  if (isLoading) {
    return <div className="text-center text-[var(--color-app-text-secondary)] py-10">Lädt …</div>;
  }

  return (
    <div className="space-y-6" data-testid="screen-drone-cinematic">
      <div>
        <Link to="/drohnen" className="text-sm text-brand-600 hover:underline">
          ← Zurück zu Drohnen
        </Link>
        <p className="text-lg font-semibold text-[var(--color-app-text)] mt-2">
          {settings ? settings.displayName : "Cinematic-Guide"}
        </p>
      </div>

      {settings ? (
        <div className="flex gap-2">
          <button
            type="button"
            className={mode === "settings" ? "btn-primary text-sm flex-1" : "btn-secondary text-sm flex-1"}
            onClick={() => setMode("settings")}
            data-testid="tab-cinematic-settings"
          >
            Kameraeinstellungen
          </button>
          <button
            type="button"
            className={mode === "moves" ? "btn-primary text-sm flex-1" : "btn-secondary text-sm flex-1"}
            onClick={() => setMode("moves")}
            data-testid="tab-cinematic-moves"
          >
            Flugmanöver
          </button>
        </div>
      ) : null}

      {mode === "settings" && settings ? (
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-app-text-secondary)]">
            Empfohlene cinematische Kamera- und Tuning-Einstellungen für die {settings.displayName}.
          </p>
          {settings.groups.map((group) => (
            <div key={group.title} className="card">
              <h2 className="text-xs font-bold uppercase tracking-wide text-brand-700 mb-2">
                {group.title}
              </h2>
              <ul className="space-y-1">
                {group.items.map((item, idx) => (
                  <li key={idx} className="text-sm text-[var(--color-app-text)] flex gap-2">
                    <span className="text-brand-500 mt-1.5 shrink-0">·</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <a
            href={settings.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-[var(--color-app-text-tertiary)] hover:underline inline-block"
            data-testid="link-cinematic-source"
          >
            Quelle: {settings.source}
          </a>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-[var(--color-app-text-secondary)]">
            {DRONE_MOVES_GUIDE.title} — funktioniert mit jeder Drohne.
          </p>
          {DRONE_MOVES_GUIDE.moves.map((move) => (
            <div key={move.title} className="card">
              <h2 className="text-xs font-bold uppercase tracking-wide text-brand-700 mb-2">
                {move.title}
              </h2>
              {move.sections.map((section, sIdx) => (
                <div key={sIdx} className={sIdx > 0 ? "mt-3" : undefined}>
                  {section.label ? (
                    <div className="text-sm font-semibold text-[var(--color-app-text-secondary)] mb-1">{section.label}</div>
                  ) : null}
                  <ul className="space-y-1">
                    {section.steps.map((step, stepIdx) => (
                      <li key={stepIdx} className="text-sm text-[var(--color-app-text)] flex gap-2">
                        <span className="text-brand-500 mt-1.5 shrink-0">·</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
          <a
            href={DRONE_MOVES_GUIDE.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-[var(--color-app-text-tertiary)] hover:underline inline-block"
            data-testid="link-moves-source"
          >
            Quelle: {DRONE_MOVES_GUIDE.source}
          </a>
        </div>
      )}
    </div>
  );
}
